import { GridWallAccessEnum, ProjectAssetEnum } from 'api/enums';
import { CctvCameraGridDataModel, GridDataModel, GridWallModel, GridWallRefreshModel } from 'api/models';
import { gridwallAccess, refreshGridwall, verifyGridwall } from 'api/services';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

interface UseGridWallReturn {
    gridWall: GridWallModel | null;
    token: string;
    isLoading: boolean;
    error: string | null;
    needsAccessCode: boolean;
    isVerifyingAccess: boolean;
    submitAccessCode: (accessCode: string) => Promise<void>;
    refreshData: () => Promise<void>;
}

export const useGridWall = (): UseGridWallReturn => {
    const { token } = useParams<{ token: string }>();
    const [gridWall, setGridWall] = useState<GridWallModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isVerifyingAccess, setIsVerifyingAccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [needsAccessCode, setNeedsAccessCode] = useState(false);
    const [accessType, setAccessType] = useState<GridWallAccessEnum | null>(null);

    // Get stored access code from localStorage
    const getStoredAccessCode = useCallback((): string | null => {
        if (!token) return null;
        return localStorage.getItem(`accessCode_${token}`);
    }, [token]);

    // Clear stored access code
    const clearStoredAccessCode = useCallback(() => {
        if (token) {
            localStorage.removeItem(`accessCode_${token}`);
        }
    }, [token]);

    // Show verify page immediately with error message
    const showVerifyPageWithError = useCallback(
        (errorMessage: string) => {
            toast.error(errorMessage);
            setNeedsAccessCode(true);
            setGridWall(null);
            setError(null);
            clearStoredAccessCode();
        },
        [clearStoredAccessCode]
    );

    // Handle verifyGridwall errors - show verify page immediately
    const handleVerifyError = useCallback(
        (error: any, context: string = '') => {
            console.error(`Error verifying gridwall ${context}:`, error);

            const errorMessage = error?.response?.data?.message || 'Access verification failed';

            // Show verify page immediately without additional API calls
            showVerifyPageWithError(errorMessage);
        },
        [showVerifyPageWithError]
    );

    // Smart update function to compare and update grid data
    const updateGridDataSmartly = useCallback(
        (current: GridWallModel, refreshData: GridWallRefreshModel) => {
            if (
                current.gridSize.rows !== refreshData.gridSize.rows ||
                current.gridSize.cols !== refreshData.gridSize.cols ||
                current.access !== refreshData.access
            ) {
                if (refreshData.access === GridWallAccessEnum.PRIVATE && !getStoredAccessCode()) {
                    showVerifyPageWithError('Access code required for private grid');
                    return null;
                }
                return 'FULL_RELOAD_REQUIRED';
            }

            const mergedGridData = current.gridData.map((currentCard, index) => {
                const refreshCard = refreshData.gridData[index];
                if (currentCard.type === ProjectAssetEnum.CAMERA_CCTV) {
                    if (currentCard.hlsUrl !== (refreshCard as CctvCameraGridDataModel).hlsUrl) {
                        return refreshCard;
                    }
                    return currentCard;
                }
                return refreshData.gridData[index] ?? currentCard;
            });

            const updatedGridWall = new GridWallModel({
                ...current.toObject(),
                footerMessage: refreshData.footerMessage,
                gridSize: refreshData.gridSize,
                access: refreshData.access,
                gridData: mergedGridData.map((item) => item.toObject()),
            });

            const hasGridDataChanges = current.gridData.some((currentCard, index) => {
                const newCard = mergedGridData[index];
                if (!newCard) return true;

                if (currentCard.customText !== newCard.customText || currentCard.project?._id !== newCard.project?._id)
                    return true;

                if (currentCard.type === ProjectAssetEnum.CAMERA_CCTV) {
                    return (
                        (currentCard as CctvCameraGridDataModel).hlsUrl !==
                        (mergedGridData[index] as CctvCameraGridDataModel)?.hlsUrl
                    );
                }

                const cur = currentCard as unknown as GridDataModel;
                const next = newCard as unknown as GridDataModel;
                return cur.latestImage?.dateTime !== next.latestImage?.dateTime || cur.camera?._id !== next.camera?._id;
            });

            if (hasGridDataChanges || current.gridData.length !== mergedGridData.length) {
                console.log('Grid data changes detected, updating specific cards');
            }

            return updatedGridWall;
        },
        [getStoredAccessCode, showVerifyPageWithError]
    );

    // Check initial access requirements
    const checkAccess = useCallback(async () => {
        if (!token) {
            setError('No token provided');
            setIsLoading(false);
            return;
        }

        try {
            setError(null);

            // First, always check the current access type
            const accessData = await gridwallAccess(token);
            setAccessType(accessData.access);

            if (accessData.access === GridWallAccessEnum.PRIVATE) {
                // Private access - check if we have stored access code
                const storedAccessCode = getStoredAccessCode();

                if (storedAccessCode) {
                    try {
                        const gridWallData = await verifyGridwall(token, storedAccessCode);
                        setGridWall(gridWallData);
                        setNeedsAccessCode(false);
                    } catch (verifyError) {
                        // Show verify page immediately on verification failure
                        handleVerifyError(verifyError, 'with stored access code');
                    }
                } else {
                    // No stored access code - show verify page
                    setNeedsAccessCode(true);
                }
            } else {
                // Public access - try to verify without access code
                try {
                    const gridWallData = await verifyGridwall(token);
                    setGridWall(gridWallData);
                    setNeedsAccessCode(false);
                } catch (verifyError) {
                    // Show verify page immediately on verification failure
                    handleVerifyError(verifyError, 'for public access');
                }
            }
        } catch (err: any) {
            console.error('Error checking gridwall access:', err);
            const errorMessage = err?.response?.data?.message || 'Failed to check access';
            toast.error(errorMessage);
            setError('Failed to check access');
            setNeedsAccessCode(false);
        } finally {
            setIsLoading(false);
        }
    }, [token, getStoredAccessCode, handleVerifyError]);

    // Submit access code for private grids
    const submitAccessCode = useCallback(
        async (accessCode: string) => {
            if (!token) return;

            setIsVerifyingAccess(true);
            setError(null);

            try {
                const gridWallData = await verifyGridwall(token, accessCode);

                // Store access code in localStorage
                localStorage.setItem(`accessCode_${token}`, accessCode);

                setGridWall(gridWallData);
                setAccessType(gridWallData.access);
                setNeedsAccessCode(false);
                toast.success('Access verified successfully');
            } catch (err: any) {
                console.error('Error verifying access code:', err);
                const errorMessage = err?.response?.data?.message || 'Invalid access code';

                // Show error and stay on verify page
                toast.error(errorMessage);
                clearStoredAccessCode();

                // Keep user on verify page - don't make additional API calls
                setNeedsAccessCode(true);
                setGridWall(null);
                setError(null);
            } finally {
                setIsVerifyingAccess(false);
            }
        },
        [token, clearStoredAccessCode]
    );

    // Improved refresh function using the dedicated refresh endpoint
    const refreshData = useCallback(async () => {
        if (!token || !gridWall) return;

        try {
            let payload: string | undefined;

            if (accessType === GridWallAccessEnum.PRIVATE) {
                payload = getStoredAccessCode() || undefined;
                if (!payload) {
                    // No access code available for private grid
                    showVerifyPageWithError('Access code required');
                    return;
                }
            }

            // Use the dedicated refresh endpoint
            const refreshedData = await refreshGridwall(token, payload);

            // Smart update based on changes
            const updateResult = updateGridDataSmartly(gridWall, refreshedData);

            if (updateResult === 'FULL_RELOAD_REQUIRED') {
                // Full reload required - re-initialize
                await checkAccess();
                return;
            }

            if (updateResult) {
                setGridWall(updateResult);
                console.log('GridWall data refreshed successfully');
            }
        } catch (err: any) {
            console.error('Error refreshing gridwall data:', err);
            const errorMessage = err?.response?.data?.message || 'Failed to refresh gridwall data';

            // If it's an access error, show verify page
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                showVerifyPageWithError(errorMessage);
            } else {
                // For other errors, just log and continue with current data
                console.warn('Refresh failed, continuing with current data:', errorMessage);
                toast.warn('Failed to refresh data, using cached version');
            }
        }
    }, [token, gridWall, accessType, getStoredAccessCode, showVerifyPageWithError, updateGridDataSmartly, checkAccess]);

    // Set up automatic refresh interval
    useEffect(() => {
        if (!gridWall || needsAccessCode) return;

        const intervalMs = gridWall.refreshInterval * 60 * 1000;

        const intervalId = setInterval(() => {
            refreshData();
        }, intervalMs);

        return () => {
            console.log('Clearing refresh interval');
            clearInterval(intervalId);
        };
    }, [gridWall, needsAccessCode, refreshData]);

    // Initial load
    useEffect(() => {
        checkAccess();
    }, [checkAccess]);

    return {
        gridWall,
        token: token || '',
        isLoading,
        error,
        needsAccessCode,
        isVerifyingAccess,
        submitAccessCode,
        refreshData,
    };
};
